// Paper dart projectile — small paper rectangle that flies straight, slight gravity.

import * as THREE from 'three';
import type { Team } from './Plane';

const BULLET_GEOM = new THREE.BufferGeometry();
BULLET_GEOM.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
   0, 0,  0.6,    -0.08, 0, -0.4,    0, 0.04, -0.4,
   0, 0,  0.6,     0.08, 0, -0.4,    0, 0.04, -0.4,
   0, 0,  0.6,     0.08, 0, -0.4,   -0.08, 0, -0.4,
]), 3));
BULLET_GEOM.computeVertexNormals();

const BULLET_MAT_BLUE = new THREE.MeshBasicMaterial({ color: 0xc8e6ff, side: THREE.DoubleSide });
const BULLET_MAT_RED = new THREE.MeshBasicMaterial({ color: 0xffc8c0, side: THREE.DoubleSide });

export class Bullet {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  team: Team;
  ownerId: string;
  ttl: number;
  damage = 1;
  mesh: THREE.Mesh;
  alive = true;

  constructor(opts: {
    position: THREE.Vector3;
    velocity: THREE.Vector3;
    team: Team;
    ownerId: string;
    ttl?: number;
  }) {
    this.position = opts.position.clone();
    this.velocity = opts.velocity.clone();
    this.team = opts.team;
    this.ownerId = opts.ownerId;
    this.ttl = opts.ttl ?? 3.5;
    this.mesh = new THREE.Mesh(BULLET_GEOM, opts.team === 'blue' ? BULLET_MAT_BLUE : BULLET_MAT_RED);
    this.mesh.position.copy(this.position);
    this.orientMesh();
  }

  update(dt: number): void {
    if (!this.alive) return;
    this.ttl -= dt;
    if (this.ttl <= 0) { this.alive = false; return; }
    // Slight gravity + drag for "paper" feel.
    this.velocity.y -= 4 * dt;
    this.velocity.multiplyScalar(1 - 0.2 * dt);
    this.position.addScaledVector(this.velocity, dt);
    this.mesh.position.copy(this.position);
    this.orientMesh();
  }

  private orientMesh(): void {
    const dir = this.velocity.clone().normalize();
    if (dir.lengthSq() < 1e-6) return;
    const up = new THREE.Vector3(0, 1, 0);
    const right = new THREE.Vector3().crossVectors(dir, up).normalize();
    const u = new THREE.Vector3().crossVectors(right, dir).normalize();
    const m = new THREE.Matrix4().makeBasis(right, u, dir.clone().multiplyScalar(-1));
    this.mesh.quaternion.setFromRotationMatrix(m);
  }
}
